import { useEffect, useState } from 'react'
import { Modal, Button, ListGroup, Form } from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import { prependBackendUrl } from '../../../gql/client'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import { GET_KANBAN_TASK_ATTACHMENTS } from '../../../gql/queries/kanban'
import {
  CREATE_KANBAN_TASK_ATTACHMENT,
  DELETE_KANBAN_TASK_ATTACHMENT,
} from '../../../gql/mutations/kanban'
import {
  ICreateKanbanTaskAttachmentRes,
  IDeleteKanbanTaskAttachmentRes,
  IKanbanTaskAttachmentsRes,
} from '../../../types/kanban'
import { parseId } from '../../../helpers'

const bullet = '•'
const space = ' '
const docEmoji = '📄'

interface Props {
  taskId?: string
  show: boolean
  onClose: () => void
}

export const ModalTaskAttachments: React.FC<Props> = ({ taskId, show, onClose }) => {
  const intl = useIntl()
  const [file, setFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')

  const { data, loading, refetch } = useQuery<IKanbanTaskAttachmentsRes>(
    GET_KANBAN_TASK_ATTACHMENTS,
    {
      variables: { id: taskId ? parseId(taskId) : null },
      skip: !taskId,
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    if (!show) {
      setFile(null)
      setDescription('')
    }
  }, [show])

  const [uploadAttachment, { loading: uploading }] = useMutation<ICreateKanbanTaskAttachmentRes>(
    CREATE_KANBAN_TASK_ATTACHMENT
  )
  const [deleteAttachment, { loading: deleting }] = useMutation<IDeleteKanbanTaskAttachmentRes>(
    DELETE_KANBAN_TASK_ATTACHMENT
  )

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const pastedFile = items[i].getAsFile()
        if (pastedFile) {
          setFile(pastedFile)
          break // Only take the first file for this simpler modal
        }
      }
    }
  }

  const handleUpload = async () => {
    if (!taskId) return
    if (!file) {
      toast.error(intl.formatMessage({id: 'leads.kanban.selectFile', defaultMessage: 'Select a file'}))
      return
    }
    try {
      const { data: uploadData } = await uploadAttachment({
        variables: {
          taskId: parseId(taskId),
          file,
          description: description || null,
        },
      })
      if (!uploadData?.createKanbanTaskAttachment.ok) {
        toast.error(
          uploadData?.createKanbanTaskAttachment.errors?.[0] || intl.formatMessage({id: 'leads.kanban.couldNotUpload', defaultMessage: 'The file could not be uploaded'})
        )
        return
      }
      toast.success(intl.formatMessage({id: 'leads.kanban.attachmentUploaded', defaultMessage: 'Attachment uploaded'}))
      setFile(null)
      setDescription('')
      refetch && refetch()
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'leads.kanban.errUploading', defaultMessage: 'Error uploading the file'}))
    }
  }

  const handleDelete = async (attachmentId: string) => {
    if (!attachmentId) return
    try {
      const { data: deleteData } = await deleteAttachment({ variables: { id: parseId(attachmentId) } })
      if (!deleteData?.deleteKanbanTaskAttachment.ok) {
        toast.error(deleteData?.deleteKanbanTaskAttachment.errors?.[0] || intl.formatMessage({id: 'leads.kanban.couldNotDelete', defaultMessage: 'Could not delete'}))
        return
      }
      toast.success(intl.formatMessage({id: 'leads.kanban.attachmentDeleted', defaultMessage: 'Attachment deleted'}))
      refetch && refetch()
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'leads.kanban.errDeletingAttachment', defaultMessage: 'Error deleting the attachment'}))
    }
  }

  const attachments = data?.kanbanTask?.attachments ?? []

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className='kanban-attachments-modal'
      onPaste={handlePaste}
    >
      <Modal.Header closeButton className='kanban-attachments-modal__header'>
        <Modal.Title><FormattedMessage id='leads.kanban.attachmentsTitle' defaultMessage='Task attachments' /></Modal.Title>
      </Modal.Header>
      <Modal.Body className='kanban-attachments-modal__body'>
        <div className='kanban-attachments-modal__upload'>
          <Form.Group className='kanban-attachments-modal__group'>
            <Form.Label><FormattedMessage id='leads.kanban.fileLabel' defaultMessage='File' /></Form.Label>
            <Form.Control
              type='file'
              className='kanban-attachments-modal__input'
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFile(event.currentTarget.files?.[0] ?? null)
              }
            />
          </Form.Group>
          <Form.Group className='kanban-attachments-modal__group'>
            <Form.Label><FormattedMessage id='common.field.description' defaultMessage='Description' /></Form.Label>
            <Form.Control
              className='kanban-attachments-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage({id: 'leads.kanban.optional', defaultMessage: 'Optional'})}
            />
          </Form.Group>
          <Button variant='primary' onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? intl.formatMessage({id: 'leads.kanban.uploading', defaultMessage: 'Uploading...'}) : intl.formatMessage({id: 'leads.kanban.upload', defaultMessage: 'Upload'})}
          </Button>
        </div>
        <div className='kanban-attachments-modal__list'>
          {loading ? (
            <div className='text-muted'>{intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}</div>
          ) : attachments.length === 0 ? (
            <div className='text-muted'>{intl.formatMessage({id: 'leads.kanban.noAttachments', defaultMessage: 'There are no attachments in the task'})}</div>
          ) : (
            <ListGroup variant='flush'>
              {attachments.map((attachment) => (
                <ListGroup.Item key={attachment.id} className='kanban-attachments-modal__item'>
                  <div className='kanban-attachments-modal__item-info d-flex align-items-center gap-3 w-100'>
                    {attachment.contentType?.startsWith('image/') ? (
                      <div className='kanban-attachments-modal__item-preview'>
                        <img
                          src={prependBackendUrl(attachment.file)}
                          alt={attachment.originalName}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className='kanban-attachments-modal__item-icon'
                        style={{ fontSize: '24px' }}
                      >
                        {docEmoji}
                      </div>
                    )}
                    <div className='flex-grow-1'>
                      <div className='d-flex align-items-center gap-2'>
                        <a
                          href={prependBackendUrl(attachment.file)}
                          target='_blank'
                          rel='noreferrer'
                          className='fw-bold'
                        >
                          {attachment.originalName || intl.formatMessage({id: 'leads.kanban.fileLabel', defaultMessage: 'File'})}
                        </a>
                        <a
                          href={prependBackendUrl(attachment.file)}
                          download={attachment.originalName}
                          className='btn btn-link btn-sm p-0 text-muted'
                          title={intl.formatMessage({id: 'leads.kanban.downloadTooltip', defaultMessage: 'Download'})}
                        >
                          <i className='bi bi-download' />
                        </a>
                      </div>
                      <small className='text-muted'>
                        {attachment.uploadedBy?.firstName} {attachment.uploadedBy?.lastName} {bullet}{space}
                        {new Date(attachment.createdAt).toLocaleString()}
                      </small>
                      {attachment.description && (
                        <div className='kanban-attachments-modal__item-desc'>
                          {attachment.description}
                        </div>
                      )}
                    </div>
                    <Button
                      size='sm'
                      variant='link'
                      className='text-danger'
                      onClick={() => handleDelete(attachment.id)}
                      disabled={deleting}
                    >
                      <i className='bi bi-trash' />
                      <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className='kanban-attachments-modal__footer'>
        <Button variant='light' onClick={onClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
